import { IDefaultSelectOption } from '@dealroadshow/uikit-select';
import { IDialInCountryFormValue } from './DialInCountriesFieldArray/interfaces';
import { ISpeakerArrayItem } from '@evercall/domain/vo/call/SpeakerArrayItem';
import { ITimeZoneOption } from '@framework/timezone/helpers';

export type TQuestionLabel = { [index: string]: string } | string;

export interface IRadioButtonAnswer {
  value: string,
  explanation?: TQuestionLabel,
  label: TQuestionLabel,
  dataTest: string,
}

export enum QuestionType {
  CheckboxOnly = 'CheckboxOnly',
  RadioOnly = 'RadioOnly',
  // PseudoRadio looks as 2-option radio button on Evercall and as checkbox on DM Portal except registrationAheadWebinar
  PseudoRadio = 'PseudoRadio',
}

export interface IQuestion {
  type: QuestionType,
  name: string,
  explanation?: TQuestionLabel,
  title: TQuestionLabel,
  radioButtonAnswers?: IRadioButtonAnswer[],
  dataTest?: string,
}

export interface IOaccQuestions {
  ATTENDEE_CHECK_IN_PROCESS: IQuestion,
  REGISTRATION_AHEAD: IQuestion,
  CUSTOM_SCRIPT: IQuestion,
  ROADSHOW_SHARED_PRESENTATION: IQuestion,
  QA_SESSION: IQuestion,
  SPEAKER_PRESENTING_PROCESS: IQuestion,
  SEPARATED_CONFERENCE_LINES: IQuestion,
  SPEAKER_DASHBOARD: IQuestion,
  CALL_RECORDING: IQuestion,
  RECORDED_REPLAY: IQuestion,
  CALL_TRANSCRIPTION: IQuestion,
}

export interface IWebinarQuestions {
  JOIN_VIA_LINK: IQuestion,
  QA_SESSION: IQuestion,
  SPEAKER_PRESENTING_PROCESS: IQuestion,
  SEPARATED_SESSION_LINES: IQuestion,
  CALL_TRANSCRIPTION: IQuestion,
  MP4: IQuestion,
  WEB_REPLAY: IQuestion,
}

export interface IMeetingQuestions {
  REGISTRATION_AHEAD: IQuestion,
  CUSTOMIZE_REGISTRATION: IQuestion,
  SLIDES_ONLY_ROADSHOW_REQUIRED: IQuestion,
  MODERATION: IQuestion,
  CALL_TRANSCRIPTION: IQuestion,
  CALL_RECORDING: IQuestion,
}

export interface ICallDetailsFormValues {
  name: string,
  dateAt: number,
  timeAt: number,
  timeZoneId: ITimeZoneOption | null,
  transactionTypeId: IDefaultSelectOption | null,
  eventTypeId: IDefaultSelectOption | null,
  estimatedDuration: string,
  approximateNumberOfParticipants: string,
  dialInCountries: IDialInCountryFormValue[],
}

export interface IOaccCommonConfigurationsFormValues {
  attendeeCheckInProcess: 'Registration' | 'Attendant_Check_In',
  roadshowSharedPresentation: string,
  roadshowSharedPresentationActivationTime: number | null,
  roadshowSharedPresentationDeactivationTime: number | null,
  qaSession: 'Verbal' | 'Written' | 'Both' | 'No',
  speakerPresentingProcess: string,
  separatedConferenceLines: string,
  speakerDashboard: string,
  callRecording: string,
  transcriptionServices: string,
  recordedReplay: string,
}

export interface IOaccConfigurationsFormValues<TBinaryQuestionAnswer>
  extends IOaccCommonConfigurationsFormValues { // Binary question is a Yes/No question
  customScript: TBinaryQuestionAnswer,
  registrationAhead: TBinaryQuestionAnswer,
}

export type TRoadshowSharedPresentationFormValues = (
  Pick<ICallDetailsFormValues, 'timeZoneId'>
  & Pick<IOaccCommonConfigurationsFormValues,
    'roadshowSharedPresentation' | 'roadshowSharedPresentationActivationTime'>
);

export interface IWebinarConfigurationsFormValues {
  /* webinarPlatformId input field is located in Call Details section
   but as an object property it is related to configurations */
  webinarPlatformId: IDefaultSelectOption,
  speakers: ISpeakerArrayItem[],
  speakerPresentingProcessWebinar: string,
  transcriptionServicesWebinar: string,
  qaSessionWebinar: 'Verbal' | 'Written' | 'Both' | 'No',
  separatedSessions: string,
  callRecordingProcess: string,
  webReplay: string,
}

export interface IMeetingCallConfigurationsFormValues<TBinaryQuestionAnswer> { // Binary question is a Yes/No question
  /* managedMeetingPlatformId input field is located in Call Details section
   but as an object property it is related to configurations */
  managedMeetingPlatformId: IDefaultSelectOption,
  registrationAheadMeeting: TBinaryQuestionAnswer,
  moderation: TBinaryQuestionAnswer,
  moderationHostName: string,
  moderationHostEmail: string,
  slidesOnlyRoadshowRequired: TBinaryQuestionAnswer,
  slidesOnlyRoadshowAdditionalNotes: string,
  recording: TBinaryQuestionAnswer,
  transcription: TBinaryQuestionAnswer,
  registrationCustomization: TBinaryQuestionAnswer,
  registrationCustomizationCompanyLogo: {
    name: string,
    url: string,
  } | null,
  registrationCustomizationBackgroundImage: {
    name: string,
    url: string,
  } | null,
}

export enum CallFormInstance {
  ScheduleYourCall = 'ScheduleYourCall', // Evercall
  CallSettingsForm = 'CallSettingsForm', // DmPortal
}
